import {zodObjectDocumentUpload} from '@/components/Helpers/validationHelpers.ts';
import type {TFunction} from 'i18next';
import moment from 'moment';
import {z} from 'zod';

export const form501Schema = (_t: TFunction<'translation', undefined>) =>
    z.object({
        residencySecondary: z.string().nullish(),
        residencyTertiary: z.array(z.string()).nullish(),
        noPermanentResidenceDate: z.custom<moment.Moment>(
            val => {
                return moment.isMoment(val);
            },
            {
                message: 'Invalid Date',
            },
        ),
        noPermanentResidenceLastAddress: z.string().nullish(),
        noPermanentResidenceLastSchool: z.string().nullish(),
        unableToProvideDocumentsSituations: z
            .array(z.string())
            .nullish()
            .transform(arr => (arr === undefined ? [] : arr)),
        unableToProvideDocumentsOther: z.string().nullish(),
        documents501: z.array(zodObjectDocumentUpload),
    });
export const form501Refine = (
    schema: ReturnType<typeof form501Schema>,
    t: TFunction<'translation', undefined>,
    livingSituation?: string | null,
) => {
    return schema
        .refine(
            formData => {
                return livingSituation === 'Homeowner' || livingSituation === 'Renter'
                    ? !!formData.residencySecondary
                    : true;
            },
            {
                message: `${t('requiredErrorFull')}`,
                path: ['residencySecondary'],
            },
        )
        .refine(
            formData => {
                return livingSituation === 'Homeowner' || livingSituation === 'Renter'
                    ? formData.residencyTertiary && formData.residencyTertiary.length >= 2
                    : true;
            },
            {
                message: `${t('twoRequired')}`,
                path: ['residencyTertiary'],
            },
        )
        .refine(
            formData => {
                return livingSituation === t('sef500Sharing') ||
                    livingSituation === t('sef500Other')
                    ? !!formData.noPermanentResidenceDate
                    : true;
            },
            {
                message: `${t('sef501Date')} ${t('requiredError')}`,
                path: ['noPermanentResidenceDate'],
            },
        )
        .refine(
            formData => {
                return livingSituation === t('sef500Sharing') ||
                    livingSituation === t('sef500Other')
                    ? !!formData.noPermanentResidenceLastAddress
                    : true;
            },
            {
                message: `${t('sef501OtherLastAddress')} ${t('requiredError')}`,
                path: ['noPermanentResidenceLastAddress'],
            },
        )
        .refine(
            formData => {
                return livingSituation === t('sef500Sharing') ||
                    livingSituation === t('sef500Other')
                    ? !!formData.noPermanentResidenceLastSchool
                    : true;
            },
            {
                message: `${t('sef501OtherLastSchool')} ${t('requiredError')}`,
                path: ['noPermanentResidenceLastSchool'],
            },
        )
        .refine(
            formData => {
                return livingSituation === t('sef500Sharing') ||
                    livingSituation === t('sef500Other')
                    ? formData.unableToProvideDocumentsSituations &&
                          formData.unableToProvideDocumentsSituations.length >= 1
                    : true;
            },
            {
                message: `${t('sef501OtherSituations')} ${t('requiredError')}`,
                path: ['unableToProvideDocumentsSituations'],
            },
        )
        .refine(
            formData => {
                return (livingSituation === t('sef500Sharing') ||
                    livingSituation === t('sef500Other')) &&
                    formData.unableToProvideDocumentsSituations !== undefined &&
                    formData.unableToProvideDocumentsSituations?.includes('Other')
                    ? !!formData.unableToProvideDocumentsOther
                    : true;
            },
            {
                message: `${t('sef501OtherOther')} ${t('requiredError')}`,
                path: ['unableToProvideDocumentsOther'],
            },
        );
};
